import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import {
  Upload, X, Check, AlertTriangle, ChevronRight, ChevronLeft, Send,
  FileDown, Plus, Trash2, CalendarPlus,
} from 'lucide-react';
import { downloadLocalFile } from '../lib/download';
import DesktopHint from './DesktopHint';

/**
 * BulkAddFixtures — the "Add many" wizard for fixtures (onboarding push
 * slice 3, Aug 2026). Sibling of BulkAddMembers: type a season's fixtures
 * into a grid, or upload CSV/XLSX → (map columns) → preview every row with
 * the server's verdict → create. All validation is server-side
 * (/events/bulk/preview and again inside /send).
 *
 * Opponent names get fuzzy-matched against the shared directory. Exact
 * matches link automatically; close ones show "Looks like you meant X?"
 * with a per-row confirm — confirming rides back to /send as opponentClubId,
 * declining keeps the typed name and creates a new opponent club.
 */

const FIELDS = [
  { key: 'date', label: 'Date' },
  { key: 'time', label: 'Kick-off' },
  { key: 'team', label: 'Team' },
  { key: 'opponent', label: 'Opponent' },
  { key: 'homeAway', label: 'Home/Away' },
  { key: 'venue', label: 'Venue (optional)' },
  { key: 'meetTime', label: 'Meet time' },
  { key: 'notes', label: 'Notes' },
];

const STATUS_STYLE = {
  ok: { color: '#2D7A4F' },
  warn: { color: '#B07D2B' },
  error: { color: '#A03A2E' },
};

const EMPTY_GRID_ROW = { date: '', time: '', team: '', opponent: '', homeAway: '', venue: '' };

// Next Saturday on/after (today + offsetWeeks). For template example rows.
function nextSaturday(offsetWeeks = 0) {
  const d = new Date();
  d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7 || 7) + offsetWeeks * 7);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

// Headers match eventBulk.js HEADER_HINTS so the column map auto-suggests.
function buildFixturesTemplateCsv(teams) {
  const t1 = teams[0]?.name || 'U11s';
  const t2 = teams[1]?.name || teams[0]?.name || '1st Team';
  const esc = (v) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const rows = [
    ['Date', 'Kick-off', 'Team', 'Opponent', 'Home/Away', 'Venue', 'Meet time', 'Notes'],
    [nextSaturday(0), '10:30', t1, 'Clevedon RFC', 'H', '', '', ''],
    [nextSaturday(1), '14:00', t2, 'Old Bristolians', 'A', '', '13:00', 'League'],
    [nextSaturday(2), '10:30', t1, 'Nailsea & Backwell', 'Away', '', '', 'Cup'],
  ];
  return rows.map(r => r.map(esc).join(',')).join('\r\n') + '\r\n';
}

// "2026-09-12T14:30" → "Sat 12 Sep, 14:30" — formatted from the string's own
// components (never via Date-parse of the full string, which would apply a
// timezone and could shift the day).
function formatLocal(local) {
  if (!local) return '';
  const [d, t] = local.split('T');
  const [y, m, day] = d.split('-').map(Number);
  const dow = new Date(y, m - 1, day).toLocaleDateString('en-GB', { weekday: 'short' });
  const mon = new Date(y, m - 1, day).toLocaleDateString('en-GB', { month: 'short' });
  return `${dow} ${day} ${mon}, ${t}`;
}

export default function BulkAddFixtures({ clubSlug, teams = [], accentColor, onClose }) {
  const [step, setStep] = useState('input'); // input | map | preview | results
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // input — the manual grid
  const [gridRows, setGridRows] = useState([
    { ...EMPTY_GRID_ROW }, { ...EMPTY_GRID_ROW }, { ...EMPTY_GRID_ROW },
  ]);
  const fileRef = useRef(null);

  // parsed file
  const [grid, setGrid] = useState(null); // { header, rows, truncated, maxRows }
  const [mapping, setMapping] = useState({});

  // defaults
  const [defaultTeamId, setDefaultTeamId] = useState(teams.length === 1 ? teams[0].id : '');
  const [defaultHomeAway, setDefaultHomeAway] = useState('home');

  // preview + send
  const [rows, setRows] = useState([]);          // raw rows sent to the API
  const [verdicts, setVerdicts] = useState(null);
  const [excluded, setExcluded] = useState(new Set());
  const [opponentChoice, setOpponentChoice] = useState({}); // index → candidateId | '' (keep typed)
  const [sendResult, setSendResult] = useState(null);

  // WT-28: Esc closes, the same as the ✕ (after a send, the hub refreshes) —
  // but not mid-send, or the hub would miss the fixtures being created.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(step === 'results'); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [step, busy, onClose]);

  const base = `/clubs/${clubSlug}/events/bulk`;
  const defaults = {
    teamId: defaultTeamId || undefined,
    homeAway: defaultHomeAway || undefined,
  };

  const fail = (err, fallback) => setError(err?.response?.data?.error || err?.message || fallback);

  // ── → preview (both paths land here) ───────────────────────────────
  const previewRows = async (nextRows) => {
    setBusy(true); setError(null);
    try {
      const res = await axios.post(`${base}/preview`, { rows: nextRows, defaults });
      setRows(nextRows);
      setVerdicts(res.data);
      setExcluded(new Set());
      setOpponentChoice({});
      setStep('preview');
    } catch (err) { fail(err, 'Could not check those fixtures.'); }
    finally { setBusy(false); }
  };

  const handleGridContinue = () => {
    const filled = gridRows.filter(r => Object.values(r).some(v => String(v).trim() !== ''));
    if (!filled.length) { setError('Add at least one fixture'); return; }
    previewRows(filled);
  };

  // ── file path ──────────────────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${base}/parse`, fd);
      setGrid(res.data);
      setMapping(res.data.suggestedMapping || {});
      setStep('map');
    } catch (err) { fail(err, 'Could not read that file.'); }
    finally { setBusy(false); }
  };

  const applyMapping = () => {
    if (mapping.date === undefined) { setError('Point at the Date column'); return; }
    if (mapping.opponent === undefined) { setError('Point at the Opponent column'); return; }
    const pick = (row, key) => (mapping[key] !== undefined ? String(row[mapping[key]] ?? '') : '');
    const mapped = grid.rows.map(r => ({
      date: pick(r, 'date'),
      time: pick(r, 'time'),
      team: pick(r, 'team'),
      opponent: pick(r, 'opponent'),
      homeAway: pick(r, 'homeAway'),
      venue: pick(r, 'venue'),
      meetTime: pick(r, 'meetTime'),
      notes: pick(r, 'notes'),
    }));
    previewRows(mapped);
  };

  // ── preview → send ─────────────────────────────────────────────────
  const handleSend = async () => {
    setBusy(true); setError(null);
    try {
      // Only rows the preview approved and the user kept, with any confirmed
      // opponent picks attached (index-aligned with verdicts; server
      // re-validates everything regardless).
      const included = rows
        .map((r, i) => {
          const choice = opponentChoice[i];
          return choice ? { ...r, opponentClubId: choice } : r;
        })
        .filter((_, i) => verdicts?.rows[i]?.status === 'ok' && !excluded.has(i));
      const res = await axios.post(`${base}/send`, { rows: included, defaults });
      setSendResult(res.data);
      setStep('results');
    } catch (err) { fail(err, 'Could not add the fixtures.'); }
    finally { setBusy(false); }
  };

  const okCount = verdicts ? verdicts.rows.filter((v, i) => v.status === 'ok' && !excluded.has(i)).length : 0;

  // QA must-fix (28 Aug): a "looks like you meant X?" row must be RESOLVED
  // before anything is created — otherwise a typo silently becomes a brand-new
  // club in the shared opponent directory. Count included rows still waiting.
  const unresolvedSuggests = verdicts
    ? verdicts.rows.filter((v, i) =>
        v.status === 'ok' && !excluded.has(i) &&
        v.opponentMatch?.status === 'suggest' && opponentChoice[i] === undefined
      ).length
    : 0;

  const defaultsBar = (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: '10px 12px', borderRadius: 8, background: 'var(--cream, #F4EFE6)', marginBottom: 12 }}>
      <div className="form-group" style={{ margin: 0, minWidth: 150 }}>
        <label className="label">Team if not in the list</label>
        <select className="input input-sm" value={defaultTeamId} onChange={e => setDefaultTeamId(e.target.value)}>
          <option value="">No default team</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
      <div className="form-group" style={{ margin: 0, minWidth: 150 }}>
        <label className="label">If Home/Away is blank</label>
        <select className="input input-sm" value={defaultHomeAway} onChange={e => setDefaultHomeAway(e.target.value)}>
          <option value="home">Home</option>
          <option value="away">Away</option>
          <option value="">Must say on each row</option>
        </select>
      </div>
    </div>
  );

  const setCell = (i, key, val) => setGridRows(prev => prev.map((r, ri) => (ri === i ? { ...r, [key]: val } : r)));

  return (
    <div className="modal-overlay" onClick={() => onClose(step === 'results')}>
      <div className="modal-card modal-md" style={{ maxWidth: 780 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add many fixtures</h3>
          {/* WT-28: labelled, 40px to tap, and Esc does the same (below). */}
          <button type="button" className="modal-close" aria-label="Close" onClick={() => onClose(step === 'results')}><X size={18} /></button>
        </div>

        {/* ── INPUT ─────────────────────────────────────────────── */}
        {step === 'input' && (
          <>
            <DesktopHint />
            <p className="modal-subtitle">
              Type the season's fixtures straight in, or upload a spreadsheet. Titles,
              meet times and respond-by dates fill themselves in — you'll check
              everything before anything is created.
            </p>
            {defaultsBar}
            <div style={{ overflowX: 'auto', border: '1px solid var(--border, #E8DFCE)', borderRadius: 8, marginBottom: 8 }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 13, minWidth: '100%' }}>
                <thead>
                  <tr>
                    {['Date', 'Kick-off', 'Team', 'Opponent', 'H/A', 'Venue (optional)', ''].map((h, i) => (
                      <th key={i} style={{ padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border, #E8DFCE)', fontSize: 12 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {gridRows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ padding: 4 }}>
                        <input type="date" className="input input-sm" value={r.date}
                          onChange={e => setCell(i, 'date', e.target.value)} style={{ minWidth: 130 }} />
                      </td>
                      <td style={{ padding: 4 }}>
                        <input type="time" className="input input-sm" value={r.time}
                          onChange={e => setCell(i, 'time', e.target.value)} style={{ minWidth: 86 }} />
                      </td>
                      <td style={{ padding: 4 }}>
                        <select className="input input-sm" value={r.team} onChange={e => setCell(i, 'team', e.target.value)} style={{ minWidth: 100 }}>
                          <option value="">{defaultTeamId ? '(default)' : '—'}</option>
                          {teams.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: 4 }}>
                        <input className="input input-sm" placeholder="e.g. Clevedon RFC" value={r.opponent}
                          onChange={e => setCell(i, 'opponent', e.target.value)} style={{ minWidth: 140 }} />
                      </td>
                      <td style={{ padding: 4 }}>
                        <select className="input input-sm" value={r.homeAway} onChange={e => setCell(i, 'homeAway', e.target.value)} style={{ minWidth: 90 }}>
                          <option value="">{defaultHomeAway ? `(${defaultHomeAway === 'home' ? 'Home' : 'Away'})` : '—'}</option>
                          <option value="home">Home</option>
                          <option value="away">Away</option>
                        </select>
                      </td>
                      <td style={{ padding: 4 }}>
                        <input className="input input-sm" placeholder="auto" value={r.venue}
                          onChange={e => setCell(i, 'venue', e.target.value)} style={{ minWidth: 120 }} />
                      </td>
                      <td style={{ padding: 4 }}>
                        <button className="btn btn-ghost btn-icon btn-sm" title="Remove row"
                          onClick={() => setGridRows(prev => (prev.length > 1 ? prev.filter((_, ri) => ri !== i) : prev))}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setGridRows(prev => [...prev, { ...EMPTY_GRID_ROW }])}>
                <Plus size={14} /> Add row
              </button>
              <button className="btn btn-primary btn-sm" style={{ background: accentColor }}
                disabled={busy} onClick={handleGridContinue}>
                {busy ? 'Checking…' : <>Check the fixtures <ChevronRight size={14} /></>}
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0 10px', color: 'var(--text-secondary)', fontSize: 13 }}>
              <div style={{ flex: 1, height: 1, background: 'var(--border, #E8DFCE)' }} />
              or
              <div style={{ flex: 1, height: 1, background: 'var(--border, #E8DFCE)' }} />
            </div>
            <div className="form-group">
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.tsv,.txt" style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files?.[0])} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-secondary" disabled={busy} onClick={() => fileRef.current?.click()}>
                  <Upload size={14} /> {busy ? 'Reading…' : 'Upload CSV or Excel'}
                </button>
                <button className="btn btn-ghost" disabled={busy}
                  onClick={() => downloadLocalFile('marakin-fixtures-template.csv', buildFixturesTemplateCsv(teams)).catch(err => fail(err, 'Could not save the template'))}>
                  <FileDown size={14} /> Download the template
                </button>
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
                The template opens in Excel or Google Sheets with the columns ready —
                Date, Kick-off, Team, Opponent, Home/Away, Venue, Meet time, Notes —
                and example fixtures showing the format (replace them with your own).
                Venue and Meet time can stay blank: home games use your home ground,
                away games use the opponent's ground where we know it, and meet time
                defaults to 30 minutes before kick-off. Up to 200 fixtures per batch.
              </p>
            </div>
          </>
        )}

        {/* ── MAP COLUMNS ───────────────────────────────────────── */}
        {step === 'map' && grid && (
          <>
            <p className="modal-subtitle">
              Tell us what each column is. {grid.rowCount} row{grid.rowCount === 1 ? '' : 's'} found
              {grid.truncated ? ` (only the first ${grid.maxRows} will be used)` : ''}.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 12 }}>
              {FIELDS.map(f => (
                <div className="form-group" key={f.key} style={{ margin: 0 }}>
                  <label className="label">{f.label}</label>
                  <select className="input input-sm" value={mapping[f.key] ?? ''}
                    onChange={e => setMapping(m => ({ ...m, [f.key]: e.target.value === '' ? undefined : Number(e.target.value) }))}>
                    <option value="">—</option>
                    {grid.header.map((h, i) => <option key={i} value={i}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid var(--border, #E8DFCE)', borderRadius: 8, marginBottom: 12 }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%' }}>
                <thead><tr>{grid.header.map((h, i) => (
                  <th key={i} style={{ padding: '6px 10px', textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border, #E8DFCE)' }}>{h}</th>
                ))}</tr></thead>
                <tbody>
                  {grid.rows.slice(0, 4).map((r, i) => (
                    <tr key={i}>{grid.header.map((_, c) => (
                      <td key={c} style={{ padding: '5px 10px', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{String(r[c] ?? '')}</td>
                    ))}</tr>
                  ))}
                </tbody>
              </table>
            </div>
            {defaultsBar}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => { setGrid(null); setStep('input'); }}>
                <ChevronLeft size={14} /> Back
              </button>
              <button className="btn btn-primary" style={{ background: accentColor }} disabled={busy} onClick={applyMapping}>
                {busy ? 'Checking…' : <>Check the fixtures <ChevronRight size={14} /></>}
              </button>
            </div>
          </>
        )}

        {/* ── PREVIEW ───────────────────────────────────────────── */}
        {step === 'preview' && verdicts && (
          <>
            <p className="modal-subtitle">
              <strong>{okCount}</strong> will be created
              {verdicts.errors > 0 && <> · <span style={STATUS_STYLE.error}>{verdicts.errors} can't be</span> (the reason is on each row)</>}.
            </p>
            <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid var(--border, #E8DFCE)', borderRadius: 8, marginBottom: 12 }}>
              {verdicts.rows.map((v, i) => {
                const suggesting = v.status === 'ok' && v.opponentMatch?.status === 'suggest';
                const choiceMade = opponentChoice[i] !== undefined;
                const hasWarn = (v.warnings || []).length > 0 || (suggesting && !choiceMade);
                return (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', borderBottom: '1px solid var(--border, #F0EAD9)', opacity: excluded.has(i) ? 0.45 : 1 }}>
                    {v.status === 'error'
                      ? <AlertTriangle size={15} style={{ ...STATUS_STYLE.error, flexShrink: 0, marginTop: 2 }} />
                      : hasWarn
                        ? <AlertTriangle size={15} style={{ ...STATUS_STYLE.warn, flexShrink: 0, marginTop: 2 }} />
                        : <Check size={15} style={{ ...STATUS_STYLE.ok, flexShrink: 0, marginTop: 2 }} />}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {v.title || `(row ${i + 1})`}
                        {v.when && <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}> · {formatLocal(v.when)}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {v.status === 'ok' && v.location && <>{v.location}</>}
                        {v.status === 'ok' && v.meetTimeLocal && <> · meet {v.meetTimeLocal}</>}
                        {v.status === 'ok' && v.rsvpByDate && <> · respond by {formatLocal(`${v.rsvpByDate}T23:59`).replace(', 23:59', '')}</>}
                        {(v.notes || []).map((n, ni) => (
                          <span key={`n${ni}`} style={v.status === 'error' ? STATUS_STYLE.error : undefined}>
                            {v.status === 'ok' || ni > 0 ? ' · ' : ''}{n}
                          </span>
                        ))}
                        {(v.warnings || []).filter(w => !suggesting || !w.startsWith('Looks like')).map((w, wi) => (
                          <span key={`w${wi}`} style={STATUS_STYLE.warn}> · {w}</span>
                        ))}
                      </div>
                      {suggesting && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 5, alignItems: 'center' }}>
                          {!choiceMade ? (
                            <>
                              <span style={{ fontSize: 12, ...STATUS_STYLE.warn }}>
                                Looks like you might mean <strong>{v.opponentMatch.candidateName}</strong>?
                              </span>
                              <button className="btn btn-secondary btn-sm"
                                onClick={() => setOpponentChoice(prev => ({ ...prev, [i]: v.opponentMatch.candidateId }))}>
                                Use {v.opponentMatch.candidateName}
                              </button>
                              <button className="btn btn-ghost btn-sm"
                                onClick={() => setOpponentChoice(prev => ({ ...prev, [i]: '' }))}>
                                Keep "{v.opponent}" (new club)
                              </button>
                            </>
                          ) : (
                            <span style={{ fontSize: 12, ...STATUS_STYLE.ok }}>
                              <Check size={12} /> {opponentChoice[i]
                                ? `Using ${v.opponentMatch.candidateName}`
                                : `"${v.opponent}" will be added as a new club`}
                              <button className="btn btn-ghost btn-sm" style={{ marginLeft: 6 }}
                                onClick={() => setOpponentChoice(prev => { const n = { ...prev }; delete n[i]; return n; })}>
                                Change
                              </button>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {v.status === 'ok' && (
                      <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }}
                        onClick={() => setExcluded(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; })}>
                        {excluded.has(i) ? 'Include' : 'Remove'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-ghost" onClick={() => setStep(grid ? 'map' : 'input')}>
                <ChevronLeft size={14} /> Back
              </button>
              <button className="btn btn-primary" style={{ background: accentColor }}
                disabled={busy || okCount === 0 || unresolvedSuggests > 0} onClick={handleSend}>
                {busy ? 'Creating…' : <><Send size={14} /> Create {okCount} fixture{okCount === 1 ? '' : 's'}</>}
              </button>
              {unresolvedSuggests > 0 && (
                <span style={{ fontSize: 12, ...STATUS_STYLE.warn }}>
                  Answer the {unresolvedSuggests} "looks like you meant…" question{unresolvedSuggests === 1 ? '' : 's'} above first.
                </span>
              )}
            </div>
          </>
        )}

        {/* ── RESULTS ───────────────────────────────────────────── */}
        {step === 'results' && sendResult && (
          <>
            <p className="modal-subtitle">
              <CalendarPlus size={14} style={STATUS_STYLE.ok} /> <strong>{sendResult.created}</strong> fixture{sendResult.created === 1 ? '' : 's'} created
              {sendResult.skipped > 0 && <> · {sendResult.skipped} skipped</>}.
              Everyone on those teams gets one notification that the fixtures are up —
              not one per fixture. You can edit or delete any of them from the Fixtures tab.
            </p>
            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--border, #E8DFCE)', borderRadius: 8, marginBottom: 12 }}>
              {sendResult.results.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '7px 10px', borderBottom: '1px solid var(--border, #F0EAD9)' }}>
                  {r.status === 'created'
                    ? <Check size={14} style={{ ...STATUS_STYLE.ok, flexShrink: 0, marginTop: 2 }} />
                    : <AlertTriangle size={14} style={{ ...STATUS_STYLE.error, flexShrink: 0, marginTop: 2 }} />}
                  <div style={{ fontSize: 13, minWidth: 0 }}>
                    <strong>{r.title || `Row ${(r.index ?? i) + 1}`}</strong>
                    {r.when && <span style={{ color: 'var(--text-secondary)' }}> · {formatLocal(r.when)}</span>}
                    {r.status === 'skipped' && (r.notes || []).length > 0 && (
                      <span style={STATUS_STYLE.error}> · {r.notes.join(' · ')}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button className="btn btn-primary" style={{ background: accentColor }} onClick={() => onClose(true)}>
              Done
            </button>
          </>
        )}

        {error && <div className="invite-error" style={{ marginTop: 10 }}><AlertTriangle size={14} /> {error}</div>}
      </div>
    </div>
  );
}
